import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Money } from "@/components/common/Money";
import { useAuth } from "@/hooks/useAuth";
import { useActiveSubscription, useSubscribe } from "@/hooks/useSubscription";
import { PLANS, MIN_BIGBOSS, type PlanId } from "@/lib/constants";
import { formatFcfa, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function Plans() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const sub = useActiveSubscription();
  const subscribe = useSubscribe();
  const [bigBossOpen, setBigBossOpen] = useState(false);

  async function pick(plan: PlanId, customAmount?: number) {
    const price = plan === "bigboss" ? customAmount ?? 0 : PLANS[plan].price;
    if ((profile?.wallet_balance ?? 0) < price) {
      toast.error(t("plans.topUpToPay"));
      return;
    }
    try {
      await subscribe.mutateAsync({ plan, customAmount });
      toast.success(t("plans.subscribed"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("plans.subscribeError"));
    }
  }

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/"><ArrowLeft className="size-[18px]" /></Link></Button>
        <h1 className="text-base font-semibold tracking-tight flex-1 text-center">Plans</h1>
        <div className="size-8" />
      </header>

      {sub.data && (
        <div className="rounded-xl border border-primary/20 bg-primary/[0.05] px-4 py-3 flex items-center gap-3">
          <Crown className="size-4 text-primary" />
          <div className="flex-1">
            <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">Plan actif</p>
            <p className="text-sm font-semibold">{PLANS[sub.data.plan].name}</p>
          </div>
          <p className="text-2xs text-muted-foreground tabular-nums">
            {t("plans.expiresOn", { date: formatDate(sub.data.expires_at) })}
          </p>
        </div>
      )}

      <div className="space-y-2">
        <PlanRow plan="standard" isActive={sub.data?.plan === "standard"} onSubscribe={() => pick("standard")} loading={subscribe.isPending} />
        <PlanRow plan="premium" isActive={sub.data?.plan === "premium"} onSubscribe={() => pick("premium")} loading={subscribe.isPending} highlight />
        <PlanRow
          plan="bigboss"
          isActive={sub.data?.plan === "bigboss"}
          onSubscribe={() => setBigBossOpen(true)}
          loading={subscribe.isPending}
          custom
          currentAmount={sub.data?.plan === "bigboss" ? sub.data.custom_amount ?? sub.data.price : null}
        />
      </div>

      <BigBossDialog
        open={bigBossOpen}
        onOpenChange={setBigBossOpen}
        currentAmount={sub.data?.plan === "bigboss" ? sub.data.custom_amount ?? sub.data.price : 0}
        onConfirm={(amount) => {
          setBigBossOpen(false);
          pick("bigboss", amount);
        }}
      />
    </div>
  );
}

interface PlanRowProps {
  plan: PlanId;
  isActive: boolean;
  onSubscribe: () => void;
  loading: boolean;
  highlight?: boolean;
  custom?: boolean;
  currentAmount?: number | null;
}

const PLAN_DESC: Record<PlanId, string> = {
  starter: "TOP30 quotidien · idéal pour commencer",
  standard: "TOP20 + TOP30 · le bon compromis",
  premium: "Tous les coupons, incluant TOP10",
  bigboss: "Mise libre · 30 % bonus parrain · remboursable 12 mois",
};

function PlanRow({ plan, isActive, onSubscribe, loading, highlight, custom, currentAmount }: PlanRowProps) {
  const { t } = useTranslation();
  const meta = PLANS[plan];
  return (
    <div className={cn(
      "rounded-xl border bg-card overflow-hidden transition-colors",
      highlight ? "border-primary/30" : "border-white/[0.06]",
      isActive && "border-success/30 bg-success/[0.03]"
    )}>
      <div className="px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold tracking-tight">{meta.name}</h3>
              {highlight && (
                <span className="inline-flex items-center rounded-md bg-primary/15 border border-primary/20 px-1.5 h-5 text-[9px] font-semibold text-primary uppercase tracking-wider">
                  Recommandé
                </span>
              )}
              {isActive && (
                <span className="inline-flex items-center rounded-md bg-success/15 border border-success/20 px-1.5 h-5 text-[9px] font-semibold text-success uppercase tracking-wider">
                  Actif
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{PLAN_DESC[plan]}</p>
          </div>
          <div className="text-right shrink-0">
            {custom ? (
              <>
                <p className="text-lg font-bold leading-none">Libre</p>
                <p className="text-2xs text-muted-foreground mt-1 tabular-nums">min {formatFcfa(MIN_BIGBOSS)}</p>
              </>
            ) : (
              <>
                <p className="text-xl font-bold leading-none num">
                  <Money amount={meta.price} />
                </p>
                <p className="text-2xs text-muted-foreground mt-1">/ mois</p>
              </>
            )}
          </div>
        </div>

        <ul className="mt-3 grid grid-cols-2 gap-y-1 text-xs">
          {meta.coupons.map((c) => (
            <li key={c} className="flex items-center gap-1.5 text-muted-foreground">
              <Check className="size-3 text-primary" />
              Coupon {c}
            </li>
          ))}
          <li className="flex items-center gap-1.5 text-muted-foreground col-span-2">
            <Check className="size-3 text-primary" />
            Bonus parrain :{" "}
            <strong className="text-foreground font-medium ml-0.5">
              {plan === "bigboss" ? "30 % mise" : meta.referralBonus > 0 ? formatFcfa(meta.referralBonus) : "—"}
            </strong>
          </li>
        </ul>

        {currentAmount ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Mise actuelle : <strong className="text-foreground font-medium">{formatFcfa(currentAmount)}</strong>
          </p>
        ) : null}

        <Button
          onClick={onSubscribe}
          variant={isActive && plan !== "bigboss" ? "outline" : "default"}
          size="default"
          className="w-full mt-4"
          loading={loading}
          disabled={isActive && plan !== "bigboss"}
        >
          {custom && isActive ? "Augmenter la mise" : isActive ? "Plan actif" : t("plans.subscribe")}
        </Button>
      </div>
    </div>
  );
}

function BigBossDialog({
  open,
  onOpenChange,
  currentAmount,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentAmount: number;
  onConfirm: (amount: number) => void;
}) {
  const [amount, setAmount] = useState(Math.max(MIN_BIGBOSS, currentAmount || MIN_BIGBOSS));
  const total = (currentAmount || 0) + amount;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Big Boss · Mise libre</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Montant</Label>
          <Input
            type="number"
            inputMode="numeric"
            min={MIN_BIGBOSS}
            value={amount}
            onChange={(e) => setAmount(Math.max(MIN_BIGBOSS, Number(e.target.value)))}
            className="text-2xl font-bold h-14 num"
          />
          <p className="text-xs text-muted-foreground">
            {currentAmount > 0
              ? `Ta mise passera de ${formatFcfa(currentAmount)} à ${formatFcfa(total)}.`
              : `Minimum ${formatFcfa(MIN_BIGBOSS)}.`}
          </p>
          <Button onClick={() => onConfirm(amount)} className="w-full" size="lg">
            Confirmer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
